import React, { useState, useEffect } from 'react';
import {
  Container,
  Form,
  InputGroup,
  Button,
  Card,
} from 'react-bootstrap';
import { Search } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../FireBase';
import { Link } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import Loading from '../components/Loading';
import '../css/SearchPage.css'

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageLoadedMap, setImageLoadedMap] = useState({});

  const searchProducts = async (searchText) => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'products'));
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const lowerQuery = searchText.toLowerCase();
      const results = data.filter(
        p =>
          p.name.toLowerCase().includes(lowerQuery) ||
          p.categoryName?.toLowerCase().includes(lowerQuery) ||
          p.authorName?.toLowerCase().includes(lowerQuery) ||
          p.sku?.toLowerCase().includes(lowerQuery)
      );
      setFiltered(results);
    } catch (err) {
      console.error('Search failed', err);
      setFiltered([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (query.trim() !== '') {
      searchProducts(query);
    } else {
      setFiltered([]);
    }
  }, [query]);

  const handleImageLoad = (productId) => {
    setImageLoadedMap(prev => ({ ...prev, [productId]: true }));
  };

  const renderEmptyState = () => (
    <div className="text-center py-5 text-muted fade-in">
      <img
        src="search.png" // Replace with actual animated image or Lottie
        alt="Start searching"
        style={{ width: '200px', marginBottom: '20px' }}
      />
      <p className="fs-5">Start typing to search products...</p>
    </div>
  );

  const renderNoResults = () => (
    <div className="text-center py-5 text-muted fade-in">
      <img
        src="search-noresult.png" // Replace with actual "no results" illustration
        alt="No results"
        style={{ width: '200px', marginBottom: '20px' }}
      />
      <p>No products found for "<strong>{query}</strong>"</p>
    </div>
  );

  return (
    <Container className="py-4 px-3  " style={{ borderRadius: '30px', marginBottom: '30px' }}>
      <div className=" py-3 z-3" style={{ borderRadius: '30px' }}>
        <h1 className="text-center mb-4" style={{
          fontFamily: 'Poppins, sans-serif',
          fontWeight: '600',
          background: 'linear-gradient(90deg, #1c5f8b ,#7c965d)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Search Products
        </h1>
        <div className="mx-auto" style={{ maxWidth: '600px' }}>
          <InputGroup className="border rounded-pill overflow-hidden shadow-sm">
            <Form.Control
              type="text"
              placeholder="Search by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="border-0"
              style={{ boxShadow: 'none' }}
            />
            <Button style={{backgroundColor:'#1c5f8b'}} className="rounded-0 px-4">
              <Search size={18} />
            </Button>
          </InputGroup>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
          <Loading />
        </div>
      ) : query.trim() === '' ? (
        renderEmptyState()
      ) : filtered.length === 0 ? (
        renderNoResults()
      ) : (
        <div className="mt-4 mx-auto fade-in " style={{ maxWidth: '650px' }}>
          {filtered.map((product) => {
            const imageLoaded = imageLoadedMap[product.id] || false;
            const firstVariation = product.variants ? product.variants[0] : null;

            return (
              <Link
                to={`/product/${product.id}`}
                key={product.id}
                className="text-decoration-none text-dark d-block"
              >
                <Card className="shadow-sm mb-4 rounded-4 border-0 p-2 card-hover">
                  <Card.Body className="d-flex align-items-center">
                    <div className="me-3 position-relative">
                      {!imageLoaded && <Skeleton width={70} height={70} />}
                      <img
                        src={product.image || product.productImage}
                        alt={product.name}
                        onLoad={() => handleImageLoad(product.id)}
                        onError={() => handleImageLoad(product.id)}
                        style={{
                          display: imageLoaded ? 'block' : 'none',
                          width: '70px',
                          height: '70px',
                          objectFit: 'cover',
                        }}
                      />
                    </div>

                    <div className="flex-grow-1">
                      <h6 className="mb-1 fw-semibold">{product.name}</h6>
                      <p className="mb-1 text-muted small">{product.categoryName} </p>
                      <p className="mb-0" style={{ color: "#1c5f8b", fontSize: '0.95rem' }}>
                        {firstVariation ? (
                          <>
                            <strong>
                              {firstVariation.unitValue} {firstVariation.unitType}
                            </strong><br />
                            <del className="text-muted me-1">
                              ₹{(parseFloat(firstVariation.price) * 1.1).toFixed(2)}
                            </del>
                            <strong>₹{firstVariation.price}</strong>
                          </>
                        ) : (
                          <>
                            <strong>
                              {product.unitValue} {product.unitType}
                            </strong><br />
                            <del className="text-muted me-1">
                              ₹{(parseFloat(product.price) * 1.1).toFixed(2)}
                            </del>
                            <strong>₹{product.price}</strong>
                          </>
                        )}
                      </p>
                    </div>
                  </Card.Body>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </Container>
  );
};

export default SearchPage;
